import { FC, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { PageContainer, RouteContext } from '@ant-design/pro-components';
import { message, Descriptions, Spin, Button } from 'antd';
import guiaService from '../../../../services/guia.service';
import type { Guia } from '../../../../interfaces/guia';
import dayjs from 'dayjs';
import { ProCard } from '@ant-design/pro-components';
import { ROUTES } from '../../../../constants/routes-constants';
import { DownloadOutlined } from '@ant-design/icons';
import { generateGuiaPdf } from '../../../guias/generatePdf';
import { formatCuit } from '../../../../utils/formatCuit';

const GuiaDetailsPage: FC<{ isSelf?: boolean }> = ({ isSelf }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [guia, setGuia] = useState<Guia | null>(null);

  useEffect(() => {
    const loadGuia = async () => {
      try {
        if (id) {
          const data = isSelf
            ? await guiaService.getSelf(Number(id))
            : await guiaService.get(Number(id));
          setGuia(data);
        }
      } catch (error) {
        message.error('Error al cargar los datos de la guía');
      } finally {
        setLoading(false);
      }
    };
    loadGuia();
  }, [id, navigate, isSelf]);

  const handleDownloadPdf = async () => {
    if (!guia) return;
    const url = await generateGuiaPdf(guia);
    const link = document.createElement('a');
    link.href = url;
    link.download = `guia-minera-${guia.numero_completo || guia.id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <PageContainer title="Cargando...">
        <Spin />
      </PageContainer>
    );
  }

  if (!guia) {
    return (
      <PageContainer
        title="Guía no encontrada"
        content="La guía solicitada no existe o no tienes permisos para verla."
      />
    );
  }

  const buqueOrigen = guia.buque || (guia.transporte as any)?.buque;
  const buqueLabel = buqueOrigen
    ? [buqueOrigen.nombre, buqueOrigen.matricula_embarcacion].filter(Boolean).join(' - ')
    : '';
  const unidadCarga =
    guia.material?.unidad?.descripcion === 'METROS CUBICOS' ? 'm3' : 'tns';

  return (
    <RouteContext.Consumer>
      {({ isMobile }) => (
        <PageContainer
          breadcrumb={{
            itemRender: (route, params, routes) => {
              const last = routes.indexOf(route) === routes.length - 1;
              return last ? (
                <span>{route.title}</span>
              ) : (
                <Link to={route.path!}>{route.title}</Link>
              );
            },
            routes: [
              {
                path: isSelf ? ROUTES.USUARIO_GUIAS_EMITIDAS_ROUTE : ROUTES.ADMIN_GUIAS_ROUTE,
                breadcrumbName: isSelf ? 'Mis guías emitidas' : 'Listado de guías'
              },
              {
                breadcrumbName:
                  guia.numero_completo ||
                  guia.numero_de_guia?.toString() ||
                  guia.id?.toString() ||
                  ''
              }
            ]
          }}
          title={`Guía: ${guia.numero_completo || guia.numero_de_guia || guia.id}`}
          extra={[
            <Button
              key="descargar"
              icon={<DownloadOutlined />}
              type="primary"
              onClick={handleDownloadPdf}
            >
              Descargar PDF
            </Button>
          ]}
        >
          <ProCard
            title="Datos de la guía"
            collapsible
            defaultCollapsed={false}
            style={{ marginBottom: 24 }}
          >
            <Descriptions
              bordered
              size="middle"
              column={isMobile ? 1 : 2}
              layout={isMobile ? 'vertical' : 'horizontal'}
            >
              <Descriptions.Item label="Nro. de guía">
                {guia.numero_completo || guia.numero_de_guia || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha de emisión">
                {guia.fecha ? dayjs(guia.fecha).format('DD/MM/YYYY HH:mm') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha de vencimiento">
                {guia.fecha_vencimiento ? dayjs(guia.fecha_vencimiento).format('DD/MM/YYYY') : '-'}
              </Descriptions.Item>
              {guia.medio_transporte === 'buque' ? (
                <Descriptions.Item label="Embarcación">
                  {buqueLabel || '-'}
                </Descriptions.Item>
              ) : (
                <Descriptions.Item label="Establecimiento">
                  {guia.establecimiento?.nombre || '-'}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Mineral">
                {guia.material?.descripcion || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Cantidad">
                {guia.cantidad || '-'} {unidadCarga}
              </Descriptions.Item>
              <Descriptions.Item label="Total de Módulos">{guia.modulos || '-'}</Descriptions.Item>
              <Descriptions.Item label="Medio de transporte">
                {guia.medio_transporte === 'camion'
                  ? 'Vehículo automotor'
                  : guia.medio_transporte === 'buque'
                    ? 'Buque'
                    : guia.medio_transporte === 'tren'
                      ? 'Tren'
                      : '-'}
              </Descriptions.Item>
              {guia.medio_transporte === 'camion' && (
                <>
                  <Descriptions.Item label="Matrícula">
                    {(guia.transporte as any)?.matricula || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Matrícula del acoplado">
                    {(guia.transporte as any)?.matricula_acoplado || '-'}
                  </Descriptions.Item>
                </>
              )}
              {guia.medio_transporte === 'tren' && (
                <>
                  <Descriptions.Item label="Matrícula del tren">
                    {(guia.transporte as any)?.matricula || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Matrícula de la locomotora">
                    {(guia.transporte as any)?.matricula_locomotora || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Matrícula de los vagones">
                    {(guia.transporte as any)?.matriculas_vagones || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Línea férrea">
                    {(guia.transporte as any)?.linea_ferrea || '-'}
                  </Descriptions.Item>
                </>
              )}
              {guia.medio_transporte === 'buque' && (
                <Descriptions.Item label="Hoja de navegación">
                  {(guia.transporte as any)?.hoja_navegacion || '-'}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Tipo de destino">
                {guia.tipo_destino?.descripcion || '-'}
              </Descriptions.Item>
            </Descriptions>
          </ProCard>

          <ProCard
            title="Datos del transportista"
            collapsible
            defaultCollapsed={false}
            style={{ marginBottom: 24 }}
          >
            <Descriptions
              bordered
              column={isMobile ? 1 : 2}
              size="middle"
              layout={isMobile ? 'vertical' : 'horizontal'}
            >
              <Descriptions.Item label="Transportista">
                {guia.transportista_razon_social || 'Propio'}
              </Descriptions.Item>
              <Descriptions.Item label="CUIT del transportista">
                {formatCuit(guia.transportista_CUIT || guia.emisor_CUIT || '') || '-'}
              </Descriptions.Item>
            </Descriptions>
          </ProCard>

          <ProCard
            title="Datos del destino"
            collapsible
            defaultCollapsed={false}
            style={{ marginBottom: 24 }}
          >
            <Descriptions
              bordered
              column={isMobile ? 1 : 2}
              size="middle"
              layout={isMobile ? 'vertical' : 'horizontal'}
            >
              <Descriptions.Item label="Nombre del destinatario">
                {guia.destino_nombre || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Dirección">
                {guia.destino_calle ? `${guia.destino_calle} ${guia.destino_numero || ''}` : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Piso/Depto">
                {guia.destino_piso || '-'} / {guia.destino_departamento || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Provincia">
                {guia.destino_provincia?.descripcion || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Partido">
                {guia.destino_partido?.descripcion || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Localidad">
                {guia.destino_localidad?.descripcion || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="CUIT del destinatario">
                {guia.destinatario_numero_identificacion || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha arribo estimado a destino">
                {guia.destino_arribo_estimado
                  ? dayjs(guia.destino_arribo_estimado).format('DD/MM/YYYY')
                  : '-'}
              </Descriptions.Item>
            </Descriptions>
          </ProCard>

          <ProCard title="Documentación respaldatoria" collapsible defaultCollapsed={false}>
            <Descriptions
              bordered
              column={isMobile ? 1 : 2}
              size="middle"
              layout={isMobile ? 'vertical' : 'horizontal'}
            >
              <Descriptions.Item label="Tipo de documentación respaldatoria">
                {guia.tipo_documentacion_respaldatoria?.descripcion || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Nro. comprobante">
                {guia.documentacion_numero_comprobante || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Importe comprobante">
                {guia.documentacion_importe != null ? `$${guia.documentacion_importe}` : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha emisión comprobante">
                {guia.documentacion_fecha_emision
                  ? dayjs(guia.documentacion_fecha_emision).format('DD/MM/YYYY')
                  : '-'}
              </Descriptions.Item>
            </Descriptions>
          </ProCard>
        </PageContainer>
      )}
    </RouteContext.Consumer>
  );
};

export default GuiaDetailsPage;
